<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { useResolveImageDownloadUrl } from 'dashboard/composables/useResolveImageDownloadUrl';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';
import TranslationToggle from '../TranslationToggle.vue';
import {
  attachmentHasTranslatedImage,
  resolveTranslatedImageAttachment,
} from '../mediaTranslation';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const {
  filteredCurrentChatAttachments,
  attachments,
  conversationId,
  showOriginalTranslationContent,
  toggleTranslatedContentView,
} = useMessageContext();
const { resolveImageDownloadUrl } = useResolveImageDownloadUrl();

const attachment = computed(() => {
  return attachments.value[0];
});

const hasTranslatedImage = computed(() =>
  attachmentHasTranslatedImage(attachment.value)
);

const displayedAttachment = computed(() =>
  resolveTranslatedImageAttachment(attachment.value, {
    showingOriginal: showOriginalTranslationContent.value,
  })
);

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);
const imageUrl = ref('');

const displayedGalleryAttachments = computed(() =>
  filteredCurrentChatAttachments.value.map(item =>
    resolveTranslatedImageAttachment(item, {
      showingOriginal: showOriginalTranslationContent.value,
    })
  )
);

const imageUrlCandidates = computed(() => {
  const {
    dataUrl,
    internalDownloadUrl,
    internal_download_url: internalDownloadUrlSnakeCase,
  } = displayedAttachment.value || {};

  return [dataUrl, internalDownloadUrl, internalDownloadUrlSnakeCase].filter(
    (url, index, urls) => url && urls.indexOf(url) === index
  );
});

const loadImageCandidate = async (index = 0) => {
  // Try the public URL first, then fall back to the Rails proxy URL if the
  // public object store URL is unavailable.
  const url = imageUrlCandidates.value[index];
  if (!url) return;

  await loadWithRetry(url);
  if (isLoaded.value) {
    imageUrl.value = url;
    return;
  }

  await loadImageCandidate(index + 1);
};

const loadImage = async () => {
  imageUrl.value = '';
  await loadImageCandidate();
};

const galleryAttachment = computed(() => ({
  ...useSnakeCase(displayedAttachment.value),
  data_url: imageUrl.value || displayedAttachment.value?.dataUrl,
}));

onMounted(() => {
  loadImage();
});

watch(imageUrlCandidates, loadImage);

const downloadAttachment = async () => {
  const { fileType, extension } = attachment.value;
  try {
    isDownloading.value = true;
    const downloadUrl = await resolveImageDownloadUrl({
      attachment: attachment.value,
      attachments: filteredCurrentChatAttachments.value,
      conversationId: conversationId.value,
    });
    if (!downloadUrl) {
      throw new Error('Missing internal download url');
    }
    await downloadFile({
      url: downloadUrl,
      type: fileType,
      extension,
    });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div v-else-if="isLoaded" class="relative group rounded-lg overflow-hidden">
      <img
        class="skip-context-menu"
        :src="imageUrl"
        :width="displayedAttachment.width"
        :height="displayedAttachment.height"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
    <TranslationToggle
      v-if="hasTranslatedImage"
      class="pt-2"
      :showing-original="showOriginalTranslationContent"
      @toggle.stop="toggleTranslatedContentView"
    />
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="galleryAttachment"
    :all-attachments="displayedGalleryAttachments"
    :conversation-id="conversationId"
    prefer-file-name
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
